import React, { useContext } from 'react';

import { ViewPageGateway } from '@confluence/view-page-gateway';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { VIEW_PAGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { fg } from '@confluence/feature-gating';
import { getStatusCodeError } from '@confluence/error-boundary/entry-points/error-types';

import { type ContentPrerequisitesProps, ContentPrerequisites } from './ContentPrerequisites';

type PagePrerequisitesProps = Pick<
	ContentPrerequisitesProps,
	'children' | 'contentQueryResult' | 'route' | 'versionOverride'
>;

export const PagePrerequisites = ({
	children,
	contentQueryResult,
	route,
	versionOverride,
}: PagePrerequisitesProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const handleTimeoutError = (error: Error) => {
		const isTimeoutError = error?.toString().includes('SocketTimeoutException');
		if (isTimeoutError) {
			experienceTracker.fail({
				name: VIEW_PAGE_EXPERIENCE,
				error,
			});
		} else {
			experienceTracker.abort({
				name: VIEW_PAGE_EXPERIENCE,
				reason: error.message,
			});
		}
		markErrorAsHandled(error);
	};

	const handleUnexpectedError = (error: Error) => {
		const isUserError = getStatusCodeError(error)?.startsWith('4');

		if (isUserError) {
			experienceTracker.abort({
				name: VIEW_PAGE_EXPERIENCE,
				reason: error.message,
			});
		} else {
			experienceTracker.fail({
				name: VIEW_PAGE_EXPERIENCE,
				error,
			});
		}
		markErrorAsHandled(error);

		throw error; // Throw to propagate to error boundary - otherwise just shows a blank screen
	};
	// When unauthorized and viewing a restricted page, user is prompted
	// with SuperAdmin gateway
	return (
		<ViewPageGateway>
			<ContentPrerequisites
				route={route}
				contentQueryResult={contentQueryResult}
				versionOverride={versionOverride}
				handleUnexpectedError={
					fg('confluence_frontend_view_page_experience_errors') ? handleUnexpectedError : undefined
				}
				handleTimeoutError={
					fg('confluence_frontend_view_page_experience_errors') ? handleTimeoutError : undefined
				}
			>
				{children}
			</ContentPrerequisites>
		</ViewPageGateway>
	);
};
